import { useEffect, useState } from "react";
import { Activity, TrendingUp, Bot, AlertTriangle, Loader2 } from "lucide-react";
import { api, apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Progress } from "@/components/ui/progress";

function fmtInt(n) {
    return (n || 0).toLocaleString("en-US");
}

function fmtPct(n) {
    if (n == null) return "0%";
    return `${Number(n).toFixed(2)}%`;
}

function StatTile({ eyebrow, value, sub }) {
    return (
        <div className="brand-card fade-up">
            <p className="eyebrow">{eyebrow}</p>
            <p className="h2 mt-2" style={{ fontSize: "1.75rem" }}>
                {value}
            </p>
            {sub && <p className="text-xs text-muted mt-1">{sub}</p>}
        </div>
    );
}

export default function Usage() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [summary, setSummary] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([
            api.get("/usage/summary"),
            api.get("/usage/recent?limit=50"),
        ])
            .then(([s, r]) => {
                if (!active) return;
                setSummary(s.data);
                setRecent(r.data || []);
                setErr("");
            })
            .catch((e) => {
                if (!active) return;
                setErr(apiErrorMessage(e));
            })
            .finally(() => {
                if (active) setLoading(false);
            });
        return () => {
            active = false;
        };
    }, []);

    if (!isAdmin) {
        return (
            <div className="page-shell">
                <p className="text-muted">
                    Usage monitoring is available to admins only.
                </p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="page-shell">
                <div className="flex items-center gap-2 text-muted">
                    <Loader2 size={16} className="animate-spin" />
                    Loading usageâ¦
                </div>
            </div>
        );
    }

    if (err) {
        return (
            <div className="page-shell">
                <p className="field-error" data-testid="usage-error">
                    {err}
                </p>
            </div>
        );
    }

    const periods = summary?.periods || [];
    const byKey = Object.fromEntries(periods.map((p) => [p.key, p]));
    const month = byKey["30d"] || byKey["all"] || { pct_of_bucket: 0, est_cost_usd: 0 };
    const softCap = summary?.soft_cap_pct ?? 80;
    const pct = Math.min(100, Math.max(0, month.pct_of_bucket || 0));
    const overSoft = pct >= softCap;

    const bucket = summary?.bucket_usd ?? 20;
    const estPer1M = summary?.est_cost_per_1m ?? 2;

    return (
        <div className="page-shell">
            <header className="fade-up">
                <p className="eyebrow">Usage monitoring</p>
                <h1 className="h1 mt-3" data-testid="usage-title">
                    LLM usage &amp; cost guard
                </h1>
                <p className="text-muted mt-2 max-w-[680px]">
                    Estimated spend against Todd's Ollama Cloud Pro ($
                    {bucket.toFixed(0)}/mo bucket). Token counts are estimated
                    (â4 chars/token) unless the provider returns exact values â
                    labeled "est." everywhere. No surprise bills: a soft-cap
                    warning fires at {softCap}% of the bucket.
                </p>
            </header>

            {/* Monthly bucket progress */}
            <div className="brand-card fade-up mt-8">
                <div className="flex items-center justify-between">
                    <p className="eyebrow">Est. monthly bucket usage</p>
                    {overSoft && (
                        <span
                            className="inline-flex items-center gap-1 text-xs font-bold"
                            style={{ color: "var(--brand-warning)" }}
                            data-testid="usage-softcap-warning"
                        >
                            <AlertTriangle size={14} />
                            Soft cap {softCap}% reached
                        </span>
                    )}
                </div>
                <div className="mt-4">
                    <Progress
                        value={pct}
                        className="h-3"
                        style={{
                            background: "var(--brand-surface)",
                        }}
                        indicatorClassName={
                            overSoft
                                ? "!bg-[var(--brand-warning)]"
                                : "bg-[var(--brand-red)]"
                        }
                        data-testid="usage-progress"
                    />
                </div>
                <div className="flex items-center justify-between mt-2">
                    <p className="text-sm font-display font-bold" data-testid="usage-pct">
                        {fmtPct(month.pct_of_bucket)} of ${bucket.toFixed(0)}/mo
                    </p>
                    <p className="text-xs text-muted" data-testid="usage-cost">
                        â ${month.est_cost_usd?.toFixed(2)} est. ({estPer1M.toFixed(2)}/$1M tokens)
                    </p>
                </div>
            </div>

            {/* Period stat tiles */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-6">
                <StatTile
                    eyebrow="Today"
                    value={byKey["today"] ? fmtInt(byKey["today"].chats) : "0"}
                    sub={
                        byKey["today"]
                            ? `${fmtInt(byKey["today"].est_tokens)} est. tokens`
                            : "no chats yet"
                    }
                />
                <StatTile
                    eyebrow="Last 7 days"
                    value={byKey["7d"] ? fmtInt(byKey["7d"].chats) : "0"}
                    sub={
                        byKey["7d"]
                            ? `${fmtInt(byKey["7d"].est_tokens)} est. tokens`
                            : ""
                    }
                />
                <StatTile
                    eyebrow="Last 30 days"
                    value={byKey["30d"] ? fmtInt(byKey["30d"].chats) : "0"}
                    sub={
                        byKey["30d"]
                            ? `${fmtInt(byKey["30d"].est_tokens)} est. tokens`
                            : ""
                    }
                />
                <StatTile
                    eyebrow="All time"
                    value={byKey["all"] ? fmtInt(byKey["all"].chats) : "0"}
                    sub={
                        byKey["all"]
                            ? `${fmtInt(byKey["all"].est_tokens)} est. tokens`
                            : ""
                    }
                />
            </div>

            {/* Recent activity */}
            <div className="brand-card fade-up mt-8">
                <div className="flex items-center gap-2 mb-4">
                    <Activity size={16} style={{ color: "var(--brand-red)" }} />
                    <p className="eyebrow">Recent activity (last 50)</p>
                </div>
                {recent.length === 0 ? (
                    <p className="text-muted text-sm">
                        No usage recorded yet. Chat or use voice and it'll show here.
                    </p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="usage-recent-table">
                            <thead>
                                <tr className="text-left text-muted border-b border-[var(--brand-surface)]">
                                    <th className="py-2 pr-4 font-semibold">When</th>
                                    <th className="py-2 pr-4 font-semibold">Type</th>
                                    <th className="py-2 pr-4 font-semibold">User</th>
                                    <th className="py-2 pr-4 font-semibold">Model / Provider</th>
                                    <th className="py-2 pr-4 font-semibold text-right">Tokens</th>
                                    <th className="py-2 pr-4 font-semibold text-right">ms</th>
                                </tr>
                            </thead>
                            <tbody>
                                {recent.map((r, i) => (
                                    <tr
                                        key={i}
                                        className="border-b border-[var(--brand-surface)] last:border-0"
                                    >
                                        <td className="py-2 pr-4 whitespace-nowrap text-muted">
                                            {new Date(r.ts).toLocaleString("en-US", {
                                                month: "short",
                                                day: "numeric",
                                                hour: "2-digit",
                                                minute: "2-digit",
                                            })}
                                        </td>
                                        <td className="py-2 pr-4">
                                            <span className="inline-flex items-center gap-1">
                                                {r.kind === "chat" ? (
                                                    <Bot size={13} />
                                                ) : (
                                                    <TrendingUp size={13} />
                                                )}
                                                {r.kind}
                                                {r.estimated ? (
                                                    <span
                                                        className="text-xs"
                                                        style={{ color: "var(--brand-muted)" }}
                                                    >
                                                        est.
                                                    </span>
                                                ) : null}
                                            </span>
                                        </td>
                                        <td className="py-2 pr-4 whitespace-nowrap">
                                            {r.user_email}
                                        </td>
                                        <td className="py-2 pr-4 whitespace-nowrap text-muted">
                                            {r.kind === "chat"
                                                ? r.model
                                                : r.provider}
                                        </td>
                                        <td className="py-2 pr-4 text-right whitespace-nowrap">
                                            {fmtInt(r.total_tokens)}
                                        </td>
                                        <td className="py-2 pr-4 text-right whitespace-nowrap text-muted">
                                            {fmtInt(r.duration_ms)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
